import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { downloadBlob } from '../../utils/blobDownload';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Download, Eye } from 'lucide-react';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';

export default function SubscriptionPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [detail, setDetail] = useState(null);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (status) params.status = status;
      const res = await api.get('subscription/payments', { params });
      const data = res.data?.data || {};
      setItems(data.items || []);
      setMeta({ pages: data.pages || 1, total: data.total || 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, [page, status]);

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`subscription/payments/${id}/invoice`, { responseType: 'blob' });
      await downloadBlob(res.data, `invoice-${id}.pdf`);
    } catch (e) {
      console.error('Invoice download failed', e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Payment History</h1>
          <p className="text-muted-foreground">View invoices and transaction details</p>
        </div>
        <Button variant="outline" asChild><Link to="/settings/subscription">Back to Subscription</Link></Button>
      </div>

      <Select value={status || 'all'} onValueChange={(v) => { setStatus(v === 'all' ? '' : v); setPage(1); }}>
        <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="paid">Paid</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="failed">Failed</SelectItem>
        </SelectContent>
      </Select>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-muted-foreground">No payments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-3">Invoice</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Cycle</th>
                    <th className="text-left p-3">Amount</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Date</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-b border-border/50">
                      <td className="p-3 font-mono text-xs">{p.invoice_number}</td>
                      <td className="p-3">{p.plan_name}</td>
                      <td className="p-3 capitalize">{p.billing_cycle?.replace('_', ' ')}</td>
                      <td className="p-3">₹{p.final_amount}</td>
                      <td className="p-3"><SubscriptionStatusBadge status={p.payment_status} /></td>
                      <td className="p-3">{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '—'}</td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => setDetail(p)}><Eye className="w-4 h-4" /></Button>
                          {p.payment_status === 'paid' && (
                            <Button size="sm" variant="ghost" onClick={() => downloadInvoice(p.id)}><Download className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {meta.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="py-2 text-sm text-muted-foreground">Page {page} of {meta.pages}</span>
          <Button variant="outline" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Payment Details</DialogTitle></DialogHeader>
          {detail && (
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted-foreground">Invoice</dt><dd>{detail.invoice_number}</dd>
              <dt className="text-muted-foreground">Plan</dt><dd>{detail.plan_name}</dd>
              <dt className="text-muted-foreground">Billing cycle</dt><dd className="capitalize">{detail.billing_cycle}</dd>
              <dt className="text-muted-foreground">Amount</dt><dd>₹{detail.amount}</dd>
              <dt className="text-muted-foreground">Discount</dt><dd>₹{detail.discount_amount}</dd>
              <dt className="text-muted-foreground">Tax</dt><dd>₹{detail.tax_amount}</dd>
              <dt className="text-muted-foreground">Final</dt><dd className="font-bold">₹{detail.final_amount}</dd>
              <dt className="text-muted-foreground">Method</dt><dd>{detail.payment_method || '—'}</dd>
              <dt className="text-muted-foreground">Transaction ID</dt><dd className="font-mono text-xs break-all">{detail.transaction_id || '—'}</dd>
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
